import { ref } from 'vue';
import { app } from '@ibgroup-tws-webapps/ratio.ui';
import { usePositions } from '@/stores/usePositions';
import { useOrderTicket } from '@/electionHub/composables/useOrderTicket';
import { getExchangeDisplayName } from '@/utils/exchangeNames';
import { resolveCloseConid } from '@/utils/closeConid';
import type { PredictionContractListContract } from '@/types/prediction.types';
import type { StrikeGroup } from '@/types/contracts.types';

export interface SpeedBumpPosition {
  exchange: string;
  displayName: string;
  side: 'yes' | 'no';
  quantity: number;
  conid: number;
  contract: PredictionContractListContract;
}

export interface SpeedBumpAction {
  label: string;
  variant: 'primary' | 'secondary';
  action: () => void;
}

export interface SpeedBumpConfig {
  title: string;
  positions: SpeedBumpPosition[];
  disclaimer: string;
  actions: SpeedBumpAction[];
}

export interface SpeedBumpInterceptParams {
  group: StrikeGroup;
  targetExchange: string;
  side: 'yes' | 'no';
  conid: number;
  intent?: string;
  eventConidExs?: string[];
}

function isNativePlatform(): boolean {
  return app.option?.clientPlatform !== 'web' && app.option?.platform !== 'web';
}

function getCloseConid(contract: PredictionContractListContract, positionSide: 'yes' | 'no'): number {
  return resolveCloseConid({
    exchange: contract.exchange,
    forecastxConid: positionSide === 'yes' ? contract.conid_no : contract.conid_yes,
    defaultConid: positionSide === 'yes' ? contract.conid_yes : contract.conid_no,
  });
}

export function useSpeedBump() {
  const visible = ref(false);
  const config = ref<SpeedBumpConfig | null>(null);

  const positionsStore = usePositions();
  const { openOrderTicket } = useOrderTicket();

  function findPositionsOnOtherExchanges(group: StrikeGroup, targetExchange: string): SpeedBumpPosition[] {
    const positions: SpeedBumpPosition[] = [];

    for (const contract of group.contracts) {
      if (contract.exchange === targetExchange) continue;

      const yesPos = positionsStore.getPositionByConid(contract.conid_yes)[0];
      if (yesPos && parseInt(yesPos.position, 10) !== 0) {
        positions.push({
          exchange: contract.exchange,
          displayName: getExchangeDisplayName(contract.exchange),
          side: 'yes',
          quantity: Math.abs(parseInt(yesPos.position, 10)),
          conid: contract.conid_yes,
          contract,
        });
      }

      const noPos = positionsStore.getPositionByConid(contract.conid_no)[0];
      if (noPos && parseInt(noPos.position, 10) !== 0) {
        positions.push({
          exchange: contract.exchange,
          displayName: getExchangeDisplayName(contract.exchange),
          side: 'no',
          quantity: Math.abs(parseInt(noPos.position, 10)),
          conid: contract.conid_no,
          contract,
        });
      }
    }

    return positions;
  }

  function buildTitle(positions: SpeedBumpPosition[]): string {
    const sides = new Set(positions.map((p) => p.side));
    const exchanges = new Set(positions.map((p) => p.exchange));

    if (sides.size > 1) {
      return 'You hold positions on other exchanges';
    }
    const side = positions[0].side === 'yes' ? 'Yes' : 'No';
    if (exchanges.size > 1) {
      return `You hold ${side} positions on other exchanges`;
    }
    return `You hold a ${side} position on another exchange`;
  }

  function buildActions(params: SpeedBumpInterceptParams, positions: SpeedBumpPosition[]): SpeedBumpAction[] {
    const actions: SpeedBumpAction[] = [];
    const targetDisplayName = getExchangeDisplayName(params.targetExchange);
    const sides = new Set(positions.map((p) => p.side));
    const isBuyingYes = params.side === 'yes';

    if (sides.size > 1) {
      // A3 / B3: mixed positions — one primary CTA per position
      for (const pos of positions) {
        const posLabel = pos.side === 'yes' ? 'Yes' : 'No';
        actions.push({
          label: `Add to ${posLabel} Position at ${pos.displayName}`,
          variant: 'primary',
          action: () => {
            visible.value = false;
            openOrderTicket(pos.contract.conid_yes, undefined, pos.contract.exchange);
          },
        });
      }
    } else if (positions.length === 1) {
      // A1 / B1: single position on one exchange
      const pos = positions[0];
      if (isBuyingYes) {
        actions.push({
          label: `Add to Yes Position at ${pos.displayName}`,
          variant: 'primary',
          action: () => {
            visible.value = false;
            openOrderTicket(pos.contract.conid_yes, undefined, pos.contract.exchange);
          },
        });
      } else {
        actions.push({
          label: `Close Yes Position at ${pos.displayName}`,
          variant: 'primary',
          action: () => {
            visible.value = false;
            const closeConid = getCloseConid(pos.contract, pos.side);
            openOrderTicket(closeConid, 'close', pos.contract.exchange);
          },
        });
      }
    } else {
      // A2 / B2: multiple same-side positions
      for (const pos of positions) {
        if (isBuyingYes) {
          actions.push({
            label: `Add to Yes Position at ${pos.displayName}`,
            variant: 'primary',
            action: () => {
              visible.value = false;
              openOrderTicket(pos.contract.conid_yes, undefined, pos.contract.exchange);
            },
          });
        } else {
          actions.push({
            label: `Close Yes Position at ${pos.displayName}`,
            variant: 'primary',
            action: () => {
              visible.value = false;
              const closeConid = getCloseConid(pos.contract, pos.side);
              openOrderTicket(closeConid, 'close', pos.contract.exchange);
            },
          });
        }
      }
    }

    // Secondary CTA: proceed with original intent
    const sideLabel = isBuyingYes ? 'Yes' : 'No';
    actions.push({
      label: `Open ${sideLabel} Position at ${targetDisplayName}`,
      variant: 'secondary',
      action: () => {
        visible.value = false;
        openOrderTicket(params.conid, params.intent, params.targetExchange, params.eventConidExs);
      },
    });

    return actions;
  }

  function intercept(params: SpeedBumpInterceptParams): boolean {
    if (!isNativePlatform()) return false;
    if (app.option?.supportsOrderTicketSpeedBump) return false;

    const positions = findPositionsOnOtherExchanges(params.group, params.targetExchange);
    if (!positions.length) return false;

    const disclaimer =
      'The exchange offerings are similar but not interchangeable. Fees, resolution times, and rules may differ. How would you like to proceed?';

    config.value = {
      title: buildTitle(positions),
      positions,
      disclaimer,
      actions: buildActions(params, positions),
    };
    visible.value = true;
    return true;
  }

  function handleAction(index: number) {
    const action = config.value?.actions[index];
    if (action) action.action();
  }

  function dismiss() {
    visible.value = false;
    config.value = null;
  }

  return {
    visible,
    config,
    intercept,
    handleAction,
    dismiss,
  };
}
